import { useState, useCallback } from "react";
import { Table, Skeleton } from "antd";
import { UsergroupAddOutlined, MailOutlined } from "@ant-design/icons";
import { fetchClientGroups, fetchClientGroupMembers } from "../../../services/customer360Service";
import EmailLogsDrawer from "./EmailLogsDrawer";
import {
  cardStyle, num, dateTime, chip, TONE, titleCase, statusChip, PanelEmpty,
  PanelHeader, PanelTable, PanelSkeleton, PanelError, usePanelData,
  SearchBox,
} from "./shared";

/**
 * Clients bundled together, with one of them as the primary contact.
 *
 * A group is how the customer mails several clients at once, so the member count
 * is the number that matters here — an empty group is one nothing will reach.
 */
/** The clients inside one group, once someone asks which they are. */
const MemberList = ({ state, onRetry }) => {
  if (state?.loading) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (state?.error) {
    return (
      <div style={{ padding: "12px 4px", fontSize: 12.5, color: "#dc2626" }}>
        Members could not be loaded.{" "}
        <button
          onClick={onRetry}
          style={{ border: "none", background: "none", color: "#6366f1", fontWeight: 600, cursor: "pointer", padding: 0 }}
        >
          Try again
        </button>
      </div>
    );
  }
  const rows = state?.rows || [];
  if (rows.length === 0) return <PanelEmpty message="This group has no clients in it." />;

  return (
    <Table
      columns={[
        {
          title: "Client", key: "client", width: 210,
          render: (_, r) => {
            const primary = r.name || r.client_name;
            const secondary = r.client_name && r.client_name !== r.name ? r.client_name : null;
            return (
              <div>
                <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{primary || "—"}</div>
                {secondary && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{secondary}</div>}
              </div>
            );
          },
        },
        {
          title: "Role", dataIndex: "role", width: 130,
          render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
        },
        {
          title: "Attorney", dataIndex: "attorney_number", width: 130,
          render: (v) => <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 600 }}>{v || "—"}</span>,
        },
        {
          title: "Email", dataIndex: "email", width: 200,
          render: (v) => <span style={{ fontSize: 12.5, color: "#475569", wordBreak: "break-all" }}>{v || "—"}</span>,
        },
        {
          title: "Mobile", dataIndex: "mobile", width: 125,
          render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{v || "—"}</span>,
        },
        {
          title: "GSTIN", dataIndex: "gstin", width: 160,
          render: (v) => <span style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
        },
        {
          title: "PAN", dataIndex: "pan", width: 120,
          render: (v) => <span style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
        },
        { title: "Status", dataIndex: "status", width: 105, render: (v) => statusChip(v) },
        {
          title: "Added", dataIndex: "assigned_at", width: 160,
          render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
        },
      ]}
      dataSource={rows}
      rowKey={(r, i) => r.id ?? i}
      pagination={false}
      size="small"
      scroll={{ x: 1340 }}
    />
  );
};

const ClientGroupsTab = ({ userId, active }) => {
  const p = usePanelData(fetchClientGroups, userId, active);
  const [mailingTo, setMailingTo] = useState(null);
  /* Members per group, fetched the first time a group is opened and kept after.
     Keyed by group id so expanding a second group does not discard the first. */
  const [members, setMembers] = useState({});
  const [expanded, setExpanded] = useState([]);

  const loadMembers = useCallback((groupId) => {
    setMembers((m) => ({ ...m, [groupId]: { loading: true, rows: null, error: null } }));
    fetchClientGroupMembers(groupId)
      .then((res) => setMembers((m) => ({ ...m, [groupId]: { loading: false, rows: res?.data || [], error: null } })))
      .catch((error) => setMembers((m) => ({ ...m, [groupId]: { loading: false, rows: null, error } })));
  }, []);

  const toggle = useCallback((groupId) => {
    setExpanded((keys) => (keys.includes(groupId)
      ? keys.filter((k) => k !== groupId)
      : [...keys, groupId]));
    // Fetched once. Re-opening a group it already has shows what it already has.
    setMembers((m) => {
      if (!m[groupId]) loadMembers(groupId);
      return m;
    });
  }, [loadMembers]);

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const columns = [
    {
      title: "Group", dataIndex: "name", width: 210,
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Members", dataIndex: "members_count", width: 120,
      /* An empty group is worth seeing, not hiding — mail sent to it reaches
         nobody, which is the kind of thing this screen is opened to find. */
      /* The badge is the control: the count is the thing someone wants to look
         inside, so it opens rather than sitting next to a separate caret. */
      render: (v, r) => {
        const n = Number(v) || 0;
        const tone = n > 0 ? TONE.blue : TONE.amber;
        if (n === 0) return chip("0 clients", tone);
        return (
          <button
            onClick={() => toggle(r.id)}
            style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}
            title={expanded.includes(r.id) ? "Hide clients" : "Show clients"}
          >
            {chip(`${num(n)} client${n === 1 ? "" : "s"}`, tone)}
          </button>
        );
      },
    },
    {
      title: "Primary client", dataIndex: "primary_client_name", width: 200,
      render: (v) => <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{v || "—"}</span>,
    },
    {
      title: "Email", dataIndex: "email", width: 210,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", wordBreak: "break-all" }}>{v || "—"}</span>,
    },
    {
      title: "Phone", dataIndex: "phone", width: 130,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{v || "—"}</span>,
    },
    {
      title: "Attorney", dataIndex: "attorney_number", width: 140,
      render: (v) => <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 600 }}>{v || "—"}</span>,
    },
    {
      title: "GSTIN", dataIndex: "gstin", width: 165,
      render: (v) => <span style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
    },
    {
      title: "Status", dataIndex: "is_active", width: 105,
      render: (v) => (Number(v) === 1 ? chip("Active", TONE.green) : chip("Inactive", TONE.slate)),
    },
    {
      title: "Created", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Emails", key: "emails", width: 110, fixed: "right",
      render: (_, r) => (
        <button
          onClick={() => setMailingTo(r)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px",
            borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff",
            color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}
        >
          <MailOutlined /> View
        </button>
      ),
    },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<UsergroupAddOutlined />}
        title="Client groups"
        subtitle="Clients bundled for mailing, with a primary contact"
        iconBg="linear-gradient(135deg,#7c3aed,#a855f7)"
        right={<SearchBox value={p.search} onChange={p.setSearch} placeholder="Search group, client, email…" width={240} />}
      />
      <PanelTable
        columns={columns}
        rows={p.rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        empty={p.searching ? "No matches for that search." : "No client groups created on this account yet."}
        scrollX={1555}
        expandable={{
          expandedRowKeys: expanded,
          onExpand: (_, r) => toggle(r.id),
          rowExpandable: (r) => Number(r.members_count) > 0,
          expandedRowRender: (r) => (
            <div style={{ padding: "6px 2px 10px" }}>
              <MemberList state={members[r.id]} onRetry={() => loadMembers(r.id)} />
            </div>
          ),
        }}
      />
      <EmailLogsDrawer
        userId={userId}
        recipient={mailingTo}
        recipientType="client_group"
        onClose={() => setMailingTo(null)}
      />
    </div>
  );
};

export default ClientGroupsTab;
